import { directSupabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';
import {
  customerMobileIdentity,
  customerRowMatchesMobile,
  isMissingPhoneNormalizedColumn,
  throwDirectoryConstraintError,
} from '../elevate/customers.phone';
import {
  mapCustomerFormToInsert,
  mapCustomerFormToUpdate,
  mapCustomerRowToUnified,
  type CustomerFormInput,
  type CustomerUpdateInput,
} from './customerMapping';

// Type for customers from the local database
export type LocalCustomer = Database['public']['Tables']['customers']['Row'];

// Type for customers from the API (same structure as local customers)
export interface ApiCustomer {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  title?: string; // Customer title
  phone_number: string;
  email: string | null;
  address: string;
  billing_address: string | null;
  organization_id: number;
  organization_name: string;
  created_at: string;
  updated_at: string;
}

// Unified customer type that can represent both sources
export interface Customer {
  id: string;
  name: string;
  title?: string; // Customer title (Mr, Mrs, Miss, etc.)
  email: string | null;
  phone: string;
  address: string;
  company?: string;
  reference?: string;
  notes?: string;
  billingAddress?: string | null;
  landline?: string | null;
  billingEmail?: string | null;
  billingContact?: string | null;
  paymentPreference?: string | null;
  vatNumber?: string | null;
  created_at: string;
  updated_at: string;
  source: 'api' | 'local'; // Track the source of the customer
}

class CustomerService {
  private supabaseUrl: string;

  constructor() {
    this.supabaseUrl = 'https://fggiylzoktmnjfslrpyw.supabase.co';
  }

  private async fetchFromEdgeFunction(endpoint: string, options: RequestInit = {}) {
    // Get the access token from localStorage (set by directAuth)
    const accessToken = localStorage.getItem('sb-access-token');
    
    const response = await fetch(`${this.supabaseUrl}/functions/v1/client-customers${endpoint}`, {
      ...options,
      headers: {
        'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const err = new Error(`Customer API error: ${response.status} ${response.statusText}`) as Error & { status?: number };
      err.status = response.status;
      throw err;
    }

    const data = await response.json();
    return data.data || data; // Handle both {data: [...]} and direct array responses
  }

  // Convert API customer to unified format
  private apiCustomerToUnified(apiCustomer: any): Customer {
    // Handle API customer response format
    const id = apiCustomer.id || apiCustomer.customer_id || 'unknown';
    const name = apiCustomer.name || 
      `${apiCustomer.first_name || ''} ${apiCustomer.last_name || ''}`.trim() || 
      'Unknown Customer';
    
    // Debug logging to see what we're getting from the API
    console.log('🔍 API Customer Data:', {
      id,
      name,
      title: apiCustomer.title,
      first_name: apiCustomer.first_name,
      last_name: apiCustomer.last_name,
      phone_number: apiCustomer.phone_number,
      email: apiCustomer.email,
      rawCustomer: apiCustomer
    });
    
    return mapCustomerRowToUnified({
      id,
      name,
      title: apiCustomer.title,
      email: apiCustomer.email,
      phone_number: apiCustomer.phone_number,
      landline_number: apiCustomer.landline_number,
      address: apiCustomer.address,
      billing_address: apiCustomer.billing_address,
      billing_email_address: apiCustomer.billing_email_address,
      billing_contact_name: apiCustomer.billing_contact_name,
      payment_preference: apiCustomer.payment_preference,
      vat_number: apiCustomer.vat_number,
      organization_id: apiCustomer.organization_id,
      organization_name: apiCustomer.organization_name,
      created_at: apiCustomer.created_at || new Date().toISOString(),
      updated_at: apiCustomer.updated_at || new Date().toISOString(),
    }, 'api');
  }

  // Convert local customer to unified format
  private localCustomerToUnified(localCustomer: LocalCustomer): Customer {
    return mapCustomerRowToUnified(localCustomer, 'local');
  }

  // Create a new customer locally (no external dependency)
  async createQuoteDockCustomer(customerData: CustomerFormInput): Promise<Customer> {
    try {
      const insert = mapCustomerFormToInsert(customerData);

      // Debug logging
      console.log('Inserting customer with organization data:', {
        organization_id: insert.organization_id,
        organization_name: insert.organization_name
      });
      
      const { data: local, error } = await directSupabase
        .from('customers')
        .insert(insert)
        .select('*')
        .single();

      if (error) {
        console.error('Error inserting customer:', error);
        throwDirectoryConstraintError(error);
      }
      
      // Debug logging after insert
      console.log('Customer inserted successfully:', {
        id: local.id,
        organization_id: local.organization_id,
        organization_name: local.organization_name
      });
      
      return this.localCustomerToUnified(local as LocalCustomer);
    } catch (error) {
      console.error('Error creating local customer:', error);
      if (error instanceof Error) throw error;
      throw new Error(String(error));
    }
  }

  // Explicit local customer creation API (used by hooks/forms)
  async createLocalCustomer(customerData: CustomerFormInput): Promise<Customer> {
    try {
      const { data: local, error } = await directSupabase
        .from('customers')
        .insert(mapCustomerFormToInsert(customerData))
        .select('*')
        .single();

      if (error) {
        throwDirectoryConstraintError(error);
      }

      return this.localCustomerToUnified(local as LocalCustomer);
    } catch (error) {
      console.error('Error creating local customer:', error);
      if (error instanceof Error) throw error;
      throw new Error(String(error));
    }
  }

  private async findLocalCustomerByMobile(phone: string): Promise<LocalCustomer | null> {
    const normalized = customerMobileIdentity(phone);
    if (normalized) {
      const byNormalized = await directSupabase
        .from('customers')
        .select('*')
        .eq('phone_normalized', normalized)
        .limit(2);
      if (!byNormalized.error) {
        return (byNormalized.data && byNormalized.data[0]) || null;
      }
      if (!isMissingPhoneNormalizedColumn(byNormalized.error)) {
        throw new Error(byNormalized.error.message);
      }
    }

    const fallback = await directSupabase.from('customers').select('*');
    if (fallback.error) throw new Error(fallback.error.message);
    return (fallback.data || []).find((row) => customerRowMatchesMobile(row, phone)) || null;
  }

  // Get customer by ID from local database
  async getLocalCustomerById(id: string): Promise<Customer | null> {
    try {
      const { data, error } = await directSupabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows returned
          return null;
        }
        throw new Error(`Failed to fetch local customer: ${error.message}`);
      }

      return this.localCustomerToUnified(data);
    } catch (error) {
      console.error('Error fetching local customer:', error);
      return null;
    }
  }

  // Search customers in local database
  async searchLocalCustomers(query: string): Promise<Customer[]> {
    try {
      const { data, error } = await directSupabase
        .from('customers')
        .select('*')
        .or(`name.ilike.%${query}%,phone_number.ilike.%${query}%,email.ilike.%${query}%`)
        .order('name');

      if (error) {
        console.error('Error searching local customers:', error);
        return [];
      }

      return data?.map((customer: any) => this.localCustomerToUnified(customer)) || [];
    } catch (error) {
      console.error('Error searching local customers:', error);
      return [];
    }
  }

  // Get all customers from local database
  async getAllLocalCustomers(): Promise<Customer[]> {
    try {
      console.log('Getting all local customers...');
      
      // Direct Supabase client query for local customers
      const { data, error } = await directSupabase
        .from('customers')
        .select('*')
        .order('name');

      if (error) {
        console.error('Direct Supabase client error:', error);
        return [];
      }

      const customers = data?.map((customer: any) => this.localCustomerToUnified(customer)) || [];
      console.log('Retrieved local customers:', customers.length);
      return customers;
    } catch (error) {
      console.error('Error fetching local customers:', error);
      return [];
    }
  }

  // Unified search across both API and local database
  async searchCustomers(query: string): Promise<Customer[]> {
    try {
      const [apiCustomers, localCustomers] = await Promise.allSettled([
        this.searchApiCustomers(query),
        this.searchLocalCustomers(query)
      ]);

      const results: Customer[] = [];

      // Add API customers if successful
      if (apiCustomers.status === 'fulfilled') {
        results.push(...apiCustomers.value);
      }

      // Add local customers if successful
      if (localCustomers.status === 'fulfilled') {
        results.push(...localCustomers.value);
      }

      // Remove duplicates based on phone number or email
      const uniqueCustomers = results.filter((customer, index, self) => 
        index === self.findIndex(c => 
          (customer.phone && c.phone === customer.phone) ||
          (customer.email && c.email === customer.email)
        )
      );

      return uniqueCustomers;
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  }

  // Search customers from API via Edge Function
  async searchApiCustomers(query: string): Promise<Customer[]> {
    try {
      // Note: The Edge Function doesn't have a search endpoint, so we'll get all customers and filter
      const customers = await this.fetchFromEdgeFunction('');
      const filteredCustomers = customers.filter((customer: ApiCustomer) => 
        customer.name.toLowerCase().includes(query.toLowerCase()) ||
        customer.phone_number.includes(query) ||
        (customer.email && customer.email.toLowerCase().includes(query.toLowerCase()))
      );
      return filteredCustomers.map((customer: ApiCustomer) => this.apiCustomerToUnified(customer));
    } catch (error) {
      return []; // Return empty array instead of throwing to allow local search to continue
    }
  }

  // Get all customers from API via Edge Function
  async getAllApiCustomers(): Promise<Customer[]> {
    try {
      const customers = await this.fetchFromEdgeFunction('');
      return customers.map((customer: ApiCustomer) => this.apiCustomerToUnified(customer));
    } catch (error) {
      return []; // Return empty array instead of throwing to allow local search to continue
    }
  }

  // Get customer by reference from API via Edge Function
  async getCustomerByReference(reference: string): Promise<Customer> {
    try {
      const customer = await this.fetchFromEdgeFunction(`/${reference}`);
      return this.apiCustomerToUnified(customer);
    } catch (error) {
      console.error('Error fetching customer from API:', error);
      throw error;
    }
  }

  // Get customer by email: Directory first, then the customer API. Missing is not an error.
  async getCustomerByEmail(email: string): Promise<Customer | null> {
    const trimmed = String(email || '').trim();
    if (!trimmed) return null;
    try {
      const localCustomer = await this.getLocalCustomerByEmail(trimmed);
      if (localCustomer) return localCustomer;
      const customer = await this.fetchFromEdgeFunction(`/email/${encodeURIComponent(trimmed)}`);
      if (!customer) return null;
      return this.apiCustomerToUnified(customer);
    } catch (error) {
      const status = (error as { status?: number } | null)?.status;
      if (status === 404) return null;
      console.error('Error fetching customer from API:', error);
      throw error;
    }
  }

  // Get customer by phone number (search both API and local)
  async getCustomerByPhone(phone: string): Promise<Customer | null> {
    try {
      const localCustomer = await this.findLocalCustomerByMobile(phone);

      if (localCustomer) {
        return this.localCustomerToUnified(localCustomer);
      }

      // If not found locally, try API via Edge Function
      try {
        const apiCustomer = await this.fetchFromEdgeFunction(`/phone/${phone}`);
        return this.apiCustomerToUnified(apiCustomer);
      } catch (apiError) {
        // If API also fails, return null
        return null;
      }
    } catch (error) {
      console.error('Error fetching customer by phone:', error);
      return null;
    }
  }

  // Update local customer
  async updateLocalCustomer(id: string, updates: CustomerUpdateInput): Promise<Customer> {
    try {
      const mapped = mapCustomerFormToUpdate(updates);

      // Generate name by concatenating first_name and last_name if either is provided
      let nameUpdate: { name?: string } = {};
      if (updates.first_name || updates.last_name) {
        const currentData = await this.getLocalCustomerById(id);
        if (currentData) {
          const firstName = updates.first_name || currentData.name.split(' ')[0] || '';
          const lastName = updates.last_name || currentData.name.split(' ').slice(1).join(' ') || '';
          nameUpdate = { name: `${firstName} ${lastName}`.trim() };
        }
      }

      // Debug logging
      console.log('🔍 Updating customer with data:', { ...mapped, ...nameUpdate });
      
      const { data, error } = await directSupabase
        .from('customers')
        .update({ ...mapped, ...nameUpdate })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Customer update error:', error);
        throwDirectoryConstraintError(error);
      }
      
      console.log('✅ Customer updated successfully:', data);

      return this.localCustomerToUnified(data);
    } catch (error) {
      console.error('Error updating local customer:', error);
      throw error;
    }
  }

  // Delete a customer from the local database
  async deleteLocalCustomer(id: string): Promise<void> {
    try {
      const { error } = await directSupabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to delete customer: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting local customer:', error);
      throw error;
    }
  }

  // Fetch all customers from API
  async fetchAllCustomersFromAPI(): Promise<Customer[]> {
    try {
      console.log('Fetching customers from your own Supabase edge function...');
      
      const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sb-access-token') || ''}`,
          'Content-Type': 'application/json'
        },
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        console.error('Your Supabase edge function response not ok:', response.status, response.statusText);
        throw new Error(`Failed to fetch customers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Your Supabase edge function response:', data);
      
      if (data.success) {
        const customers = (data.data || []).map((localCustomer: LocalCustomer) => this.localCustomerToUnified(localCustomer));
        console.log('Local customers converted:', customers.length);
        return customers;
      } else {
        console.error('Your Supabase edge function returned unsuccessful response:', data);
        throw new Error('API returned unsuccessful response');
      }
    } catch (error) {
      console.error('Error fetching customers from your API:', error);
      return [];
    }
  }

  // Get customer by phone number from local database
  async getLocalCustomerByPhone(phoneNumber: string): Promise<Customer | null> {
    try {
      const data = await this.findLocalCustomerByMobile(phoneNumber);
      return data ? this.localCustomerToUnified(data) : null;
    } catch (error) {
      console.error('Error fetching local customer by phone:', error);
      return null;
    }
  }

  // Get customer by email from local database
  async getLocalCustomerByEmail(email: string): Promise<Customer | null> {
    try {
      const { data, error } = await directSupabase
        .from('customers')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows returned
          return null;
        }
        throw new Error(`Failed to fetch local customer: ${error.message}`);
      }

      return this.localCustomerToUnified(data);
    } catch (error) {
      console.error('Error fetching local customer by email:', error);
      return null;
    }
  }
}

// Export a singleton instance
export const customerService = new CustomerService(); 